import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { RewardsV1Service } from '../services/rewardsV1Service';
import { QuestionService } from '../services/questionService';
import { CardService } from '../services/cardService';
import type { QuestionRecord } from '../types/question';
import type { Card } from '../types/card';

type EditableFields = {
  name: string;
  search: string;
  status: string;
  bank_name: string;
  card_name: string;
  card_id: string;
  question_refs: string[];
  description: string;
  conditions: string[];
  base_value: string;
  base_spend_unit: string;
  accelerated_value: string;
  accelerated_spend_unit: string;
  acceleration_factor: string;
  effective_rate_per_inr: string;
  reward_max_value: string;
  limit: string;
};

type RewardsV1EditUpdatePayload = {
  name?: string;
  search?: string;
  status?: string;
  bank_name?: string;
  card_name?: string;
  cardId?: string;
  questions?: string[];
  rewards?: {
    description?: string;
    conditions?: string[];
    max_value?: number | null;
    limit?: number | null;
    dynamic?: {
      base_value?: number | null;
      base_spend_unit?: number | null;
      accelerated_value?: number | null;
      accelerated_spend_unit?: number | null;
      acceleration_factor?: number | null;
      effective_rate_per_inr?: number | null;
    };
  };
};

function pickString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function pickCardId(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const anyV = value as any;
    const id = anyV?._id ?? anyV?.id ?? anyV?.cardId;
    if (typeof id === 'string') return id;
    if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  }
  return '';
}

function pickStatus(value: unknown): string {
  const s = pickString(value).trim().toLowerCase();
  if (s === 'active' || s === 'inactive') return s;
  return 'active';
}

function pickStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        if (typeof v === 'string') return v;
        if (typeof v === 'number' && Number.isFinite(v)) return String(v);
        if (typeof v === 'boolean') return String(v);
        return '';
      })
      .filter((v) => v.trim().length > 0);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([k, v]) => {
      if (typeof v === 'string') return `${k}: ${v}`;
      if (typeof v === 'number' && Number.isFinite(v)) return `${k}: ${v}`;
      if (typeof v === 'boolean') return `${k}: ${v}`;
      return `${k}: ${JSON.stringify(v)}`;
    });
  }

  return [];
}

function pickQuestionRefs(value: unknown): string[] {
  if (Array.isArray(value)) {
    const refs = value
      .map((v) => {
        if (typeof v === 'string') return v;
        if (typeof v === 'number' && Number.isFinite(v)) return String(v);
        if (v && typeof v === 'object') {
          const anyV = v as any;
          const id = anyV?._id ?? anyV?.id;
          if (typeof id === 'string') return id;
          if (typeof id === 'number' && Number.isFinite(id)) return String(id);
        }
        return '';
      })
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(refs));
  }
  return [];
}

function pickNumberString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') return value;
  return '';
}

function sanitizeInteger(raw: string): string {
  return raw.replace(/[^0-9]/g, '');
}

function sanitizeDecimal(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDotIndex = cleaned.indexOf('.');
  if (firstDotIndex === -1) return cleaned;
  const beforeDot = cleaned.slice(0, firstDotIndex + 1);
  const afterDot = cleaned.slice(firstDotIndex + 1).replace(/\./g, '');
  return beforeDot + afterDot;
}

function toIntOrNull(raw: string): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function toFloatOrNull(raw: string): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

function normalizeConditions(values: string[]): string[] {
  return values.map((v) => v.trim()).filter((v) => v.length > 0);
}

function tokenizeSearch(value: string): string[] {
  // Reward.search contains keywords like: "ott" or "general-fees".
  // We want to match questions by *exact* search key, so preserve hyphens and
  // only split the reward field by commas/whitespace.
  const tokens = (value || '')
    .toLowerCase()
    .split(/[\s,]+/g)
    .map((t) => t.trim())
    .filter(Boolean);
  return Array.from(new Set(tokens));
}

function matchesQuestionFilter(q: QuestionRecord, rawQuery: string): boolean {
  const query = (rawQuery || '').trim().toLowerCase();
  if (!query) return true;
  const haystack = [
    q.question,
    q.name,
    q.questionId,
    q.search,
    q.group,
    q.category,
  ]
    .filter((v): v is string => typeof v === 'string')
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function buildChangedPayload(original: EditableFields, form: EditableFields): RewardsV1EditUpdatePayload {
  const payload: RewardsV1EditUpdatePayload = {};

  if (form.name !== original.name) {
    payload.name = form.name;
  }

  if (form.search !== original.search) {
    payload.search = form.search;
  }

  if (form.status !== original.status) {
    payload.status = form.status;
  }

  if (form.bank_name !== original.bank_name) {
    payload.bank_name = form.bank_name;
  }

  if (form.card_name !== original.card_name) {
    payload.card_name = form.card_name;
  }

  if (form.card_id !== original.card_id) {
    payload.cardId = form.card_id || undefined;
  }

  if (JSON.stringify(form.question_refs) !== JSON.stringify(original.question_refs)) {
    payload.questions = Array.from(new Set(form.question_refs.map((s) => s.trim()).filter(Boolean)));
  }

  const rewards: RewardsV1EditUpdatePayload['rewards'] = {};
  let hasRewards = false;

  if (form.description !== original.description) {
    rewards.description = form.description;
    hasRewards = true;
  }

  if (JSON.stringify(form.conditions) !== JSON.stringify(original.conditions)) {
    rewards.conditions = normalizeConditions(form.conditions);
    hasRewards = true;
  }

  if (form.reward_max_value !== original.reward_max_value) {
    rewards.max_value = toIntOrNull(form.reward_max_value);
    hasRewards = true;
  }

  if (form.limit !== original.limit) {
    rewards.limit = toIntOrNull(form.limit);
    hasRewards = true;
  }

  const dynamic: NonNullable<NonNullable<RewardsV1EditUpdatePayload['rewards']>['dynamic']> = {};
  let hasDynamic = false;

  if (form.base_value !== original.base_value) {
    dynamic.base_value = toIntOrNull(form.base_value);
    hasDynamic = true;
  }
  if (form.base_spend_unit !== original.base_spend_unit) {
    dynamic.base_spend_unit = toIntOrNull(form.base_spend_unit);
    hasDynamic = true;
  }
  if (form.accelerated_value !== original.accelerated_value) {
    dynamic.accelerated_value = toIntOrNull(form.accelerated_value);
    hasDynamic = true;
  }
  if (form.accelerated_spend_unit !== original.accelerated_spend_unit) {
    dynamic.accelerated_spend_unit = toIntOrNull(form.accelerated_spend_unit);
    hasDynamic = true;
  }
  if (form.acceleration_factor !== original.acceleration_factor) {
    dynamic.acceleration_factor = toFloatOrNull(form.acceleration_factor);
    hasDynamic = true;
  }
  if (form.effective_rate_per_inr !== original.effective_rate_per_inr) {
    dynamic.effective_rate_per_inr = toFloatOrNull(form.effective_rate_per_inr);
    hasDynamic = true;
  }

  if (hasDynamic) {
    rewards.dynamic = dynamic;
    hasRewards = true;
  }

  if (hasRewards) {
    payload.rewards = rewards;
  }

  return payload;
}

function buildFullPayload(form: EditableFields): RewardsV1EditUpdatePayload {
  return {
    name: form.name,
    search: form.search,
    status: form.status,
    bank_name: form.bank_name,
    card_name: form.card_name,
    cardId: form.card_id || undefined,
    questions: Array.from(new Set(form.question_refs.map((s) => s.trim()).filter(Boolean))),
    rewards: {
      description: form.description,
      conditions: normalizeConditions(form.conditions),
      max_value: toIntOrNull(form.reward_max_value),
      limit: toIntOrNull(form.limit),
      dynamic: {
        base_value: toIntOrNull(form.base_value),
        base_spend_unit: toIntOrNull(form.base_spend_unit),
        accelerated_value: toIntOrNull(form.accelerated_value),
        accelerated_spend_unit: toIntOrNull(form.accelerated_spend_unit),
      },
    },
  };
}

function computeEffectiveRatePerInr(acceleratedValueRaw: string, acceleratedSpendUnitRaw: string): string {
  const acceleratedValue = Number(acceleratedValueRaw);
  const acceleratedSpendUnit = Number(acceleratedSpendUnitRaw);

  if (!Number.isFinite(acceleratedValue) || !Number.isFinite(acceleratedSpendUnit) || acceleratedSpendUnit <= 0) {
    return '';
  }

  const rate = acceleratedValue / acceleratedSpendUnit;
  const fixed = rate.toFixed(6);
  return fixed.replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
}

function computeAccelerationFactor(acceleratedValueRaw: string, baseValueRaw: string): string {
  const acceleratedValue = Number(acceleratedValueRaw);
  const baseValue = Number(baseValueRaw);

  if (!Number.isFinite(acceleratedValue) || !Number.isFinite(baseValue) || baseValue <= 0) {
    return '';
  }

  const factor = acceleratedValue / baseValue;
  const fixed = factor.toFixed(6);
  return fixed.replace(/\.0+$/, '').replace(/(\.[0-9]*?)0+$/, '$1');
}

function computeRpEarned(spendRaw: string, acceleratedValueRaw: string, acceleratedSpendUnitRaw: string): number | null {
  const spend = Number(spendRaw);
  const acceleratedValue = Number(acceleratedValueRaw);
  const acceleratedSpendUnit = Number(acceleratedSpendUnitRaw);

  if (
    !Number.isFinite(spend) ||
    !Number.isFinite(acceleratedValue) ||
    !Number.isFinite(acceleratedSpendUnit) ||
    spend < 0 ||
    acceleratedValue <= 0 ||
    acceleratedSpendUnit <= 0
  ) {
    return null;
  }

  // RP Earned = spend / (accelerated_spend_unit / accelerated_value)
  return (spend * acceleratedValue) / acceleratedSpendUnit;
}

const RewardsV1Edit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isDuplicate = location.pathname.startsWith('/rewards/new/');

  type TabKey = 'details' | 'conditions' | 'questions';
  const [activeTab, setActiveTab] = React.useState<TabKey>('details');

  const [questionsLoading, setQuestionsLoading] = React.useState(false);
  const [questionsError, setQuestionsError] = React.useState<string | null>(null);
  const [questions, setQuestions] = React.useState<QuestionRecord[]>([]);
  const [questionFilter, setQuestionFilter] = React.useState('');
  const questionsRequestIdRef = React.useRef(0);

  const [cardInput, setCardInput] = React.useState('');
  const [cardInputTouched, setCardInputTouched] = React.useState(false);
  const [cardDropdownOpen, setCardDropdownOpen] = React.useState(false);
  const [cardLoading, setCardLoading] = React.useState(false);
  const [cardError, setCardError] = React.useState<string | null>(null);
  const [cardOptions, setCardOptions] = React.useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = React.useState<Card | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState<string | null>(null);
  const [didDuplicate, setDidDuplicate] = React.useState<boolean>(false);

  const [original, setOriginal] = React.useState<EditableFields | null>(null);
  const [form, setForm] = React.useState<EditableFields>({
    name: '',
    search: '',
    status: 'active',
    bank_name: '',
    card_name: '',
    card_id: '',
    question_refs: [],
    description: '',
    conditions: [],
    base_value: '',
    base_spend_unit: '',
    accelerated_value: '',
    accelerated_spend_unit: '',
    acceleration_factor: '',
    effective_rate_per_inr: '',
    reward_max_value: '',
    limit: '',
  });

  const [exampleSpend, setExampleSpend] = React.useState<string>('15000');

  React.useEffect(() => {
    const shouldLoadQuestions =
      activeTab === 'questions' || (activeTab === 'details' && form.question_refs.length > 0);
    if (!shouldLoadQuestions) return;
    if (questions.length > 0) return;

    const requestId = (questionsRequestIdRef.current += 1);
    let cancelled = false;
    (async () => {
      try {
        setQuestionsLoading(true);
        setQuestionsError(null);
        const res = await QuestionService.listActiveQuestions();
        if (cancelled || questionsRequestIdRef.current !== requestId) return;
        setQuestions(res.data || []);
      } catch (e) {
        if (cancelled || questionsRequestIdRef.current !== requestId) return;
        setQuestionsError(e instanceof Error ? e.message : 'Failed to load questions');
      } finally {
        if (cancelled || questionsRequestIdRef.current !== requestId) return;
        setQuestionsLoading(false);
      }
    })();

    return () => {
      // React dev StrictMode runs effects twice; ensure we don't leave loading stuck.
      cancelled = true;
      if (questionsRequestIdRef.current === requestId) {
        setQuestionsLoading(false);
      }
    };
  }, [activeTab, form.question_refs.length, questions.length]);

  React.useEffect(() => {
    if (cardInputTouched) return;
    const next = [form.bank_name, form.card_name].filter(Boolean).join(' — ');
    setCardInput(next);
  }, [cardInputTouched, form.bank_name, form.card_name]);

  React.useEffect(() => {
    if (!cardDropdownOpen) return;

    let isMounted = true;
    const handle = setTimeout(async () => {
      try {
        setCardLoading(true);
        setCardError(null);

        const search = cardInput.trim();
        const res = await CardService.getCards({
          page: 1,
          limit: 20,
          ...(search ? { search } : {}),
        });

        if (!isMounted) return;
        setCardOptions(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setCardError(e instanceof Error ? e.message : 'Failed to load cards');
        setCardOptions([]);
      } finally {
        if (!isMounted) return;
        setCardLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [cardDropdownOpen, cardInput]);

  const searchTokens = React.useMemo(() => tokenizeSearch(form.search), [form.search]);

  const matchedCountByToken = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of searchTokens) counts[t] = 0;
    if (!searchTokens.length || !questions.length) return counts;

    const tokenSet = new Set(searchTokens);
    for (const q of questions) {
      const key = (q.search || '').trim().toLowerCase();
      if (!key) continue;
      if (tokenSet.has(key)) counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [questions, searchTokens]);

  const { matchedQuestions, otherQuestions } = React.useMemo(() => {
    const tokenSet = new Set(searchTokens);
    const normalized = questions.filter((q) => matchesQuestionFilter(q, questionFilter));

    const matched: QuestionRecord[] = [];
    const other: QuestionRecord[] = [];

    for (const q of normalized) {
      const key = (q.search || '').trim().toLowerCase();
      if (key && tokenSet.has(key)) matched.push(q);
      else other.push(q);
    }

    const sorter = (a: QuestionRecord, b: QuestionRecord) => {
      const ag = (a.group || '').toLowerCase();
      const bg = (b.group || '').toLowerCase();
      if (ag !== bg) return ag.localeCompare(bg);
      const ac = (a.category || '').toLowerCase();
      const bc = (b.category || '').toLowerCase();
      if (ac !== bc) return ac.localeCompare(bc);
      const an = (a.name || a.question || '').toLowerCase();
      const bn = (b.name || b.question || '').toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      return (a.questionId || '').localeCompare(b.questionId || '');
    };

    matched.sort(sorter);
    other.sort(sorter);
    return { matchedQuestions: matched, otherQuestions: other };
  }, [questions, questionFilter, searchTokens]);

  const selectedQuestionsData = React.useMemo(() => {
    if (!form.question_refs.length) return { items: [] as QuestionRecord[], missingIds: [] as string[] };
    const map = new Map(questions.map((q) => [q._id, q] as const));
    const items: QuestionRecord[] = [];
    const missingIds: string[] = [];
    for (const id of form.question_refs) {
      const match = map.get(id);
      if (match) items.push(match);
      else missingIds.push(id);
    }
    return { items, missingIds };
  }, [form.question_refs, questions]);

  React.useEffect(() => {
    if (!id) {
      document.title = isDuplicate ? 'Duplicate Reward' : 'Edit Reward';
      return;
    }
    document.title = isDuplicate ? `Duplicate Reward • ${id}` : `Edit Reward • ${id}`;
  }, [id, isDuplicate]);

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!id) {
        setError('Missing id');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const res = await RewardsV1Service.getById(id);
        const data: any = res.data;

        const next: EditableFields = {
          name: pickString(data?.name),
          search: pickString(data?.search),
          status: pickStatus((data as any)?.status),
          bank_name: pickString(data?.bank_name || data?.cardId?.bankName),
          card_name: pickString(data?.card_name || data?.cardId?.name),
          card_id: pickCardId((data as any)?.cardId ?? (data as any)?.card_id),
          question_refs: pickQuestionRefs(
            (data as any)?.questions ?? (data as any)?.question_refs ?? (data as any)?.question_ids ?? (data as any)?.questionIds
          ),
          description: pickString(data?.rewards?.description),
          conditions: pickStringArray(data?.rewards?.conditions),
          base_value: sanitizeInteger(
            pickNumberString(data?.rewards?.dynamic?.base_value ?? data?.rewards?.base_value)
          ),
          base_spend_unit: sanitizeInteger(
            pickNumberString(data?.rewards?.dynamic?.base_spend_unit ?? data?.rewards?.base_spend_unit)
          ),
          accelerated_value: sanitizeInteger(
            pickNumberString(data?.rewards?.dynamic?.accelerated_value ?? data?.rewards?.accelerated_value)
          ),
          accelerated_spend_unit: sanitizeInteger(
            pickNumberString(
              data?.rewards?.dynamic?.accelerated_spend_unit ?? data?.rewards?.accelerated_spend_unit
            )
          ),
          acceleration_factor: '',
          effective_rate_per_inr: '',
          reward_max_value: sanitizeInteger(
            pickNumberString((data as any)?.rewards?.max_value ?? (data as any)?.rewards?.maxValue)
          ),
          limit: sanitizeInteger(pickNumberString(data?.rewards?.limit)),
        };

        next.acceleration_factor =
          computeAccelerationFactor(next.accelerated_value, next.base_value) ||
          sanitizeDecimal(
            pickNumberString(data?.rewards?.dynamic?.acceleration_factor ?? data?.rewards?.acceleration_factor)
          );

        next.effective_rate_per_inr =
          computeEffectiveRatePerInr(next.accelerated_value, next.accelerated_spend_unit) ||
          sanitizeDecimal(
            pickNumberString(
              data?.rewards?.dynamic?.effective_rate_per_inr ?? data?.rewards?.effective_rate_per_inr
            )
          );

        if (!isMounted) return;
        setOriginal(next);
        setForm(next);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load reward');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  React.useEffect(() => {
    // Re-enable Duplicate when navigating to a new reward/new/:id or when leaving duplicate mode.
    setDidDuplicate(false);
  }, [id, isDuplicate]);

  const reset = () => {
    if (original) setForm(original);
  };

  const save = async () => {
    if (!id || !original) return;

    const payload = isDuplicate ? buildFullPayload(form) : buildChangedPayload(original, form);
    const hasAnyChanges = Object.keys(payload).length > 0;

    // In edit mode we avoid empty payload updates.
    if (!isDuplicate && !hasAnyChanges) return;

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(null);

      if (isDuplicate) {
        await RewardsV1Service.upsertNewById(id, payload);
        setSaveSuccess('Saved (new)');
        setDidDuplicate(true);
      } else {
        await RewardsV1Service.updateEditById(id, payload);
        setSaveSuccess('Saved');
      }

      setOriginal(form);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = React.useMemo(() => {
    if (!original) return false;
    return (
      form.name !== original.name ||
      form.search !== original.search ||
      form.status !== original.status ||
      form.bank_name !== original.bank_name ||
      form.card_name !== original.card_name ||
      form.card_id !== original.card_id ||
      JSON.stringify(form.question_refs) !== JSON.stringify(original.question_refs) ||
      form.description !== original.description ||
      JSON.stringify(form.conditions) !== JSON.stringify(original.conditions) ||
      form.base_value !== original.base_value ||
      form.base_spend_unit !== original.base_spend_unit ||
      form.accelerated_value !== original.accelerated_value ||
      form.accelerated_spend_unit !== original.accelerated_spend_unit ||
      form.acceleration_factor !== original.acceleration_factor ||
      form.effective_rate_per_inr !== original.effective_rate_per_inr ||
      form.reward_max_value !== original.reward_max_value ||
      form.limit !== original.limit
    );
  }, [form, original]);

  const previewPayload = React.useMemo(() => {
    if (!original) return null;
    return isDuplicate ? buildFullPayload(form) : buildChangedPayload(original, form);
  }, [form, isDuplicate, original]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isDuplicate ? 'Duplicate Reward' : form.name}</h1>
          <p className="mt-1 text-sm text-gray-500 break-all">{form.bank_name} - {form.card_name} | {id}</p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/rewards"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </Link>
          {id ? (
            <Link
              to={`/rewards/json-edit/${encodeURIComponent(id)}`}
              className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
              target="_blank"
              rel="noreferrer"
            >
              Json
            </Link>
          ) : null}
          <button
            onClick={reset}
            disabled={!hasChanges}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </button>
          <button
            onClick={save}
            disabled={saving || (isDuplicate && didDuplicate) || (!isDuplicate && !hasChanges)}
            className="inline-flex items-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            title={isDuplicate ? 'Save as new reward' : !hasChanges ? 'No changes to save' : 'Save changes'}
          >
            {saving ? 'Saving…' : isDuplicate ? 'Duplicate' : 'Save'}
          </button>
        </div>
      </div>

      {saveError ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{saveError}</div>
      ) : null}

      {saveSuccess ? (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 text-sm">
          {saveSuccess}
        </div>
      ) : null}

      {id ? (
        <details className="rounded-md border border-gray-200 bg-white">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-800">
            Preview payload
            <span className="ml-2 text-xs font-normal text-gray-500">({isDuplicate ? 'full' : 'diff-only'})</span>
          </summary>
          <div className="px-4 pb-4">
            <div className="text-xs text-gray-500">
              {isDuplicate ? (
                <>
                  POST <span className="font-mono">/api/rewardsv1</span>
                </>
              ) : (
                <>
                  PUT <span className="font-mono">/api/rewardsv1/edit/{id}</span>
                </>
              )}
            </div>
            <pre className="mt-2 max-h-72 overflow-auto rounded bg-gray-50 p-3 text-[11px] leading-5 text-gray-800 border border-gray-200">
              {JSON.stringify(previewPayload || {}, null, 2)}
            </pre>
            {!isDuplicate && !hasChanges ? (
              <div className="mt-2 text-xs text-gray-500">No changes yet — payload is empty.</div>
            ) : null}
          </div>
        </details>
      ) : null}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="mt-2 text-gray-500">Loading…</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-t-md border ${
                  activeTab === 'details'
                    ? 'border-gray-200 border-b-white bg-white text-gray-900'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('conditions')}
                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-t-md border ${
                  activeTab === 'conditions'
                    ? 'border-gray-200 border-b-white bg-white text-gray-900'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Conditions
                <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  {form.conditions.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('questions')}
                className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-t-md border ${
                  activeTab === 'questions'
                    ? 'border-gray-200 border-b-white bg-white text-gray-900'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Questions
              </button>
            </nav>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {activeTab === 'details' ? (
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="Reward name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Search</label>
                <input
                  value={form.search}
                  onChange={(e) => setForm((p) => ({ ...p, search: e.target.value }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="Search keywords (e.g., ott, dining, fuel)"
                />
                <div className="mt-1 text-xs text-gray-500">Used for matching in reward option search.</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 bg-white"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Credit card (search & select)</label>
                <div className="relative mt-1">
                  <input
                    value={cardInput}
                    onChange={(e) => {
                      setSelectedCard(null);
                      setCardInputTouched(true);
                      setCardInput(e.target.value);
                      setCardDropdownOpen(true);
                    }}
                    onFocus={() => setCardDropdownOpen(true)}
                    onBlur={() => {
                      setTimeout(() => setCardDropdownOpen(false), 150);
                    }}
                    placeholder="Type bank or card name (e.g., hdfc)"
                    className="block w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />

                  {cardDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
                      {cardLoading && (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>
                      )}

                      {cardError && !cardLoading && (
                        <div className="px-3 py-2 text-sm text-red-700">{cardError}</div>
                      )}

                      {!cardLoading && !cardError && cardOptions.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No cards found.</div>
                      )}

                      {!cardLoading && !cardError && cardOptions.length > 0 && (
                        <div className="max-h-64 overflow-auto">
                          {cardOptions.map((c) => (
                            <button
                              key={c._id}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                              }}
                              onClick={() => {
                                setSelectedCard(c);
                                setCardInputTouched(true);
                                setCardInput(`${c.bankName} — ${c.name}`);
                                setCardDropdownOpen(false);
                                setForm((p) => ({
                                  ...p,
                                  bank_name: c.bankName || '',
                                  card_name: c.name || '',
                                  card_id: c._id || '',
                                }));
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedCard?._id === c._id ? 'bg-gray-50' : ''}`}
                            >
                              <div className="text-gray-900">{c.bankName} — {c.name}</div>
                              <div className="text-xs text-gray-500 break-all">{c._id}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedCard ? (
                  <div className="mt-2 text-xs text-gray-500">
                    Selected: {selectedCard.bankName} — {selectedCard.name}
                  </div>
                ) : null}
                <div className="mt-1 text-xs text-gray-500">
                  Current: {form.bank_name || '—'} — {form.card_name || '—'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="Reward description"
                  rows={6}
                />
              </div>

              <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Selected questions</div>
                    <div className="mt-1 text-xs text-gray-500">Linked to this reward.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('questions')}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Manage
                  </button>
                </div>

                {form.question_refs.length ? (
                  <div className="mt-3 space-y-3">
                    {selectedQuestionsData.items.map((q) => (
                      <div key={q._id} className="rounded border border-blue-100 bg-white p-3">
                        <div className="text-sm font-medium text-gray-900 break-words">{q.question || q.name || '—'}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          <span className="font-medium text-gray-700">Id:</span> <span className="font-mono">{q._id}</span>
                          {' • '}
                          <span className="font-medium text-gray-700">Key:</span> {q.questionId || '—'}
                          {' • '}
                          <span className="font-medium text-gray-700">Group:</span> {q.group || '—'}
                          {' • '}
                          <span className="font-medium text-gray-700">Category:</span> {q.category || '—'}
                        </div>
                        {q.search ? (
                          <div className="mt-1 text-xs text-gray-600 break-words">
                            <span className="font-medium text-gray-700">Question search:</span> {q.search}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {selectedQuestionsData.missingIds.length ? (
                      <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        <div className="font-medium">Some selected questions are not in the loaded list:</div>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {selectedQuestionsData.missingIds.map((id) => (
                            <span key={id} className="inline-flex items-center rounded-full bg-white border border-amber-200 px-2.5 py-1 text-[11px] text-amber-900">
                              <span className="font-mono">{id}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-gray-600">No questions selected yet.</div>
                )}
              </div>
            </div>
            ) : activeTab === 'conditions' ? (
            <div className="md:col-span-2 space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Conditions</div>
                    <div className="mt-1 text-xs text-gray-500">These are shown in reward option search.</div>
                  </div>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700 hover:bg-blue-100"
                    onClick={() => setForm((p) => ({ ...p, conditions: [...p.conditions, ''] }))}
                    title="Add condition"
                    aria-label="Add condition"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path
                        fillRule="evenodd"
                        d="M10 4a1 1 0 011 1v4h4a1 1 0 110 2h-4v4a1 1 0 11-2 0v-4H5a1 1 0 110-2h4V5a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                <div className="mt-3 space-y-2">
                  {form.conditions.length === 0 ? (
                    <div className="text-xs text-gray-500">No conditions yet.</div>
                  ) : (
                    form.conditions.map((value, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <input
                          value={value}
                          onChange={(e) =>
                            setForm((p) => {
                              const next = [...p.conditions];
                              next[idx] = e.target.value;
                              return { ...p, conditions: next };
                            })
                          }
                          className="block w-full rounded border border-gray-300 px-3 py-2"
                          placeholder={`Condition ${idx + 1}`}
                        />

                        <div className="mt-1 flex flex-col gap-1">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            onClick={() =>
                              setForm((p) => {
                                if (idx === 0) return p;
                                const next = [...p.conditions];
                                [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                return { ...p, conditions: next };
                              })
                            }
                            disabled={idx === 0}
                            title="Move up"
                            aria-label="Move condition up"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path
                                fillRule="evenodd"
                                d="M10 3a1 1 0 01.707.293l5 5a1 1 0 11-1.414 1.414L11 6.414V16a1 1 0 11-2 0V6.414L5.707 9.707A1 1 0 114.293 8.293l5-5A1 1 0 0110 3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>

                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            onClick={() =>
                              setForm((p) => {
                                if (idx >= p.conditions.length - 1) return p;
                                const next = [...p.conditions];
                                [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                                return { ...p, conditions: next };
                              })
                            }
                            disabled={idx >= form.conditions.length - 1}
                            title="Move down"
                            aria-label="Move condition down"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path
                                fillRule="evenodd"
                                d="M10 17a1 1 0 01-.707-.293l-5-5a1 1 0 011.414-1.414L9 13.586V4a1 1 0 112 0v9.586l3.293-3.293a1 1 0 111.414 1.414l-5 5A1 1 0 0110 17z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>

                        <button
                          type="button"
                          className="mt-1 inline-flex items-center justify-center rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700 hover:bg-red-100"
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              conditions: p.conditions.filter((_, i) => i !== idx),
                            }))
                          }
                          title="Delete condition"
                          aria-label="Delete condition"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path
                              fillRule="evenodd"
                              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2h1v10a2 2 0 002 2h6a2 2 0 002-2V6h1a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zm-1 6a1 1 0 012 0v7a1 1 0 11-2 0V8zm4 0a1 1 0 10-2 0v7a1 1 0 102 0V8z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            ) : (
            <div className="md:col-span-2">
              <div className="space-y-4">
                {questionsLoading ? (
                  <div className="text-center py-10">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <p className="mt-2 text-gray-500">Loading questions…</p>
                  </div>
                ) : questionsError ? (
                  <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{questionsError}</div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-md border border-gray-200 bg-white p-4">
                      <div className="text-xs font-medium text-gray-700">Search questions</div>
                      <input
                        value={questionFilter}
                        onChange={(e) => setQuestionFilter(e.target.value)}
                        placeholder="Search by question text, key, group, category, or search token"
                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        Showing {matchedQuestions.length + otherQuestions.length} / {questions.length} questions
                      </div>
                    </div>

                    {matchedQuestions.length > 0 ? (
                      <div className="rounded-md border border-blue-200 bg-blue-50">
                        <div className="px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                          <div className="text-sm font-semibold text-blue-900">Matched questions</div>
                          <div className="text-xs text-blue-800">{matchedQuestions.length} match(es)</div>
                        </div>
                        <div className="px-4 py-3">
                          <div className="space-y-3">
                            {matchedQuestions.map((q) => (
                              <div
                                key={q._id}
                                className={`rounded border p-3 ${
                                  form.question_refs.includes(q._id)
                                    ? 'border-blue-300 bg-blue-100'
                                    : 'border-blue-100 bg-white'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                <div className="text-sm font-medium text-gray-900 break-words">{q.question || q.name || '—'}</div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setForm((p) => {
                                        const exists = p.question_refs.includes(q._id);
                                        const next = exists
                                          ? p.question_refs.filter((id) => id !== q._id)
                                          : [...p.question_refs, q._id];
                                        return { ...p, question_refs: next };
                                      })
                                    }
                                    className={`shrink-0 inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md ${
                                      form.question_refs.includes(q._id)
                                        ? 'border-blue-300 bg-white text-blue-800 hover:bg-blue-50'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    {form.question_refs.includes(q._id) ? 'Remove' : 'Add'}
                                  </button>
                                </div>
                                <div className="mt-1 text-xs text-gray-600">
                                  <span className="font-medium text-gray-700">Key:</span> {q.questionId || '—'}
                                  {' • '}
                                  <span className="font-medium text-gray-700">Group:</span> {q.group || '—'}
                                  {' • '}
                                  <span className="font-medium text-gray-700">Category:</span> {q.category || '—'}
                                </div>
                                {q.search ? (
                                  <div className="mt-1 text-xs text-gray-600 break-words">
                                    <span className="font-medium text-gray-700">Question search:</span> {q.search}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="rounded-md border border-gray-200 bg-white">
                      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                        <div className="text-sm font-semibold text-gray-900">Other questions</div>
                        <div className="text-xs text-gray-500">{otherQuestions.length} question(s)</div>
                      </div>
                      <div className="px-4 py-3">
                        {otherQuestions.length === 0 ? (
                          <div className="text-sm text-gray-600">No questions to show.</div>
                        ) : (
                          <div className="space-y-3">
                            {otherQuestions.map((q) => (
                              <div
                                key={q._id}
                                className={`rounded border p-3 ${
                                  form.question_refs.includes(q._id)
                                    ? 'border-blue-200 bg-blue-50'
                                    : 'border-gray-100 bg-gray-50'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                <div className="text-sm font-medium text-gray-900 break-words">{q.question || q.name || '—'}</div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setForm((p) => {
                                        const exists = p.question_refs.includes(q._id);
                                        const next = exists
                                          ? p.question_refs.filter((id) => id !== q._id)
                                          : [...p.question_refs, q._id];
                                        return { ...p, question_refs: next };
                                      })
                                    }
                                    className={`shrink-0 inline-flex items-center px-2 py-1 border text-xs font-medium rounded-md ${
                                      form.question_refs.includes(q._id)
                                        ? 'border-blue-300 bg-white text-blue-800 hover:bg-blue-50'
                                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    {form.question_refs.includes(q._id) ? 'Remove' : 'Add'}
                                  </button>
                                </div>
                                <div className="mt-1 text-xs text-gray-600">
                                  <span className="font-medium text-gray-700">Key:</span> {q.questionId || '—'}
                                  {' • '}
                                  <span className="font-medium text-gray-700">Group:</span> {q.group || '—'}
                                  {' • '}
                                  <span className="font-medium text-gray-700">Category:</span> {q.category || '—'}
                                </div>
                                {q.search ? (
                                  <div className="mt-1 text-xs text-gray-600 break-words">
                                    <span className="font-medium text-gray-700">Question search:</span> {q.search}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                )}
              </div>
            </div>
            )}

            <div className={`md:col-span-1 space-y-4 ${activeTab === 'details' ? '' : 'hidden md:block opacity-60'}`}>
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="text-sm font-medium text-gray-800">Example calculation</div>
                <div className="mt-2 space-y-2 text-xs text-gray-700">
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-gray-800">Spend (editable)</div>
                      <div className="text-[11px] text-gray-500">e.g. 15000</div>
                    </div>
                    <input
                      value={exampleSpend}
                      inputMode="numeric"
                      onChange={(e) => setExampleSpend(sanitizeInteger(e.target.value))}
                      className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-xs"
                      placeholder="15000"
                    />
                  </div>

                  <div>
                    <div className="font-medium text-gray-800">Return =&gt;{' '}
                      {(() => {
                        const factor = computeAccelerationFactor(form.accelerated_value, form.base_value);
                        return factor ? `${factor}X` : '—';
                      })()}
                      </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">Effective Rate (%) ={' '}
                      {(() => {
                        const v = Number(form.accelerated_value);
                        const u = Number(form.accelerated_spend_unit);
                        if (!Number.isFinite(v) || !Number.isFinite(u) || u <= 0) return '—';
                        return `${(v / u * 100).toFixed(2)}%`;
                      })()}
                    </div>
                  </div>

                  <div>
                    <div className="font-medium text-gray-800">Capped RP ={' '}
                      {(() => {
                        const rp = computeRpEarned(exampleSpend, form.accelerated_value, form.accelerated_spend_unit);
                        if (rp === null) return '—';
                        const limit = Number(form.limit);
                        if (Number.isFinite(limit) && limit > 0) return Math.min(rp, limit).toFixed(2);
                        return rp.toFixed(2);
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Base Value</label>
                <input
                  value={form.base_value}
                  inputMode="numeric"
                  onChange={(e) =>
                    setForm((p) => {
                      const base_value = sanitizeInteger(e.target.value);
                      const acceleration_factor = computeAccelerationFactor(p.accelerated_value, base_value);
                      return { ...p, base_value, acceleration_factor };
                    })
                  }
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Base Spend Unit</label>
                <input
                  value={form.base_spend_unit}
                  inputMode="numeric"
                  onChange={(e) => setForm((p) => ({ ...p, base_spend_unit: sanitizeInteger(e.target.value) }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="150"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Accelerated Value</label>
                <input
                  value={form.accelerated_value}
                  inputMode="numeric"
                  onChange={(e) =>
                    setForm((p) => {
                      const accelerated_value = sanitizeInteger(e.target.value);
                      const effective_rate_per_inr = computeEffectiveRatePerInr(
                        accelerated_value,
                        p.accelerated_spend_unit
                      );
                      const acceleration_factor = computeAccelerationFactor(accelerated_value, p.base_value);
                      return { ...p, accelerated_value, effective_rate_per_inr, acceleration_factor };
                    })
                  }
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Accelerated Spend Unit</label>
                <input
                  value={form.accelerated_spend_unit}
                  inputMode="numeric"
                  onChange={(e) =>
                    setForm((p) => {
                      const accelerated_spend_unit = sanitizeInteger(e.target.value);
                      const effective_rate_per_inr = computeEffectiveRatePerInr(
                        p.accelerated_value,
                        accelerated_spend_unit
                      );
                      return { ...p, accelerated_spend_unit, effective_rate_per_inr };
                    })
                  }
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="150"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Acceleration Factor</label>
                <input
                  value={form.acceleration_factor}
                  inputMode="decimal"
                  disabled
                  readOnly
                  className="mt-1 block w-full rounded border border-gray-200 px-3 py-2 bg-gray-50 text-gray-700"
                  placeholder="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Effective Rate per INR</label>
                <input
                  value={form.effective_rate_per_inr}
                  inputMode="decimal"
                  disabled
                  readOnly
                  className="mt-1 block w-full rounded border border-gray-200 px-3 py-2 bg-gray-50 text-gray-700"
                  placeholder="0.1333"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">RewardMaxValue</label>
                <input
                  value={form.reward_max_value}
                  inputMode="numeric"
                  onChange={(e) => setForm((p) => ({ ...p, reward_max_value: sanitizeInteger(e.target.value) }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Limit (Monthly Cap)</label>
                <input
                  value={form.limit}
                  inputMode="numeric"
                  onChange={(e) => setForm((p) => ({ ...p, limit: sanitizeInteger(e.target.value) }))}
                  className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
                  placeholder="1000"
                />
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {hasChanges ? 'You have unsaved changes.' : 'No changes yet.'}
              </div>
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-700"
                onClick={() => navigate(0)}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsV1Edit;
